import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, ComposedChart, Line } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CashFlow = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [cashflowData, setCashflowData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchCashflowData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchCashflowData = async (projectId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/cashflow`);
      setCashflowData(response.data);
    } catch (error) {
      console.error('Error fetching cashflow data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value}`;
  };

  // Prepare chart data
  const chartData = cashflowData?.monthly?.map(m => ({
    month: m.month,
    plannedIncome: m.planned_income,
    actualIncome: m.actual_income || 0,
    plannedCost: m.planned_cost,
    actualCost: m.actual_cost || 0,
    netPlanned: m.planned_income - m.planned_cost,
    netActual: (m.actual_income || 0) - (m.actual_cost || 0)
  })) || [];

  if (loading && !cashflowData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen" data-testid="cashflow-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/')} className="p-1">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('cashflow.title')}</h1>
            <p className="text-xs text-gray-500">{t('cashflow.subtitle')}</p>
          </div>
        </div>
        
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('common.selectProject')} />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.project_id} value={project.project_id}>
                {project.project_code} - {project.project_name.substring(0, 25)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Summary Card */}
        {cashflowData?.cumulative && (
          <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium opacity-80 mb-3">{t('cashflow.summary')}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs opacity-70">{t('cashflow.totalInvoiced')}</p>
                  <p className="text-xl font-bold">{formatCurrency(cashflowData.cumulative.total_invoiced)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-70">{t('cashflow.totalCollected')}</p>
                  <p className="text-xl font-bold">{formatCurrency(cashflowData.cumulative.total_collected)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-70">{t('cashflow.outstandingAR')}</p>
                  <p className="text-lg font-semibold text-amber-200">{formatCurrency(cashflowData.cumulative.outstanding_receivables)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-70">{t('cashflow.totalSpent')}</p>
                  <p className="text-lg font-semibold">{formatCurrency(cashflowData.cumulative.total_spent)}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/20">
                <div className="flex justify-between items-center">
                  <span className="text-sm opacity-80">{t('cashflow.netCashPosition')}</span>
                  <span className="text-xl font-bold">
                    {formatCurrency(cashflowData.cumulative.total_collected - cashflowData.cumulative.total_spent)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Income vs Cost Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('cashflow.monthlyIncomeVsCost')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ left: 0, right: 10 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 8 }} angle={-45} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-2 shadow-lg rounded border text-xs">
                            <p className="font-semibold mb-1">{label}</p>
                            {payload.map((entry, index) => (
                              <p key={index} style={{ color: entry.color }}>
                                {entry.name}: {formatCurrency(entry.value)}
                              </p>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '9px' }} />
                  <Bar dataKey="plannedIncome" name={t('cashflow.plannedIncome')} fill="#a5b4fc" />
                  <Bar dataKey="actualIncome" name={t('cashflow.actualIncome')} fill="#22c55e" />
                  <Line type="monotone" dataKey="plannedCost" name={t('cashflow.plannedCost')} stroke="#f97316" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="actualCost" name={t('cashflow.actualCost')} stroke="#ef4444" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Net Cash Flow Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('cashflow.netCashFlow')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ left: 0, right: 10 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 8 }} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelStyle={{ fontSize: '10px' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="netPlanned" 
                    name={t('cashflow.plannedNet')} 
                    stroke="#8b5cf6" 
                    fill="#c4b5fd" 
                    fillOpacity={0.5}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="netActual" 
                    name={t('cashflow.actualNet')} 
                    stroke="#22c55e" 
                    fill="#86efac" 
                    fillOpacity={0.5}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Details */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{t('cashflow.monthlyDetails')}</h3>
          {cashflowData?.monthly?.map((month, index) => (
            <Card key={index}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm text-gray-900">{month.month}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    (month.actual_income || 0) >= month.planned_income ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {month.actual_income ? t('cashflow.completed') : t('cashflow.projected')}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">{t('cashflow.plannedIncome')}</span>
                      <span className="font-medium text-blue-600">{formatCurrency(month.planned_income)}</span>
                    </div>
                    {month.actual_income && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">{t('cashflow.actualIncome')}</span>
                        <span className="font-medium text-green-600">{formatCurrency(month.actual_income)}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">{t('cashflow.plannedCost')}</span>
                      <span className="font-medium text-orange-600">{formatCurrency(month.planned_cost)}</span>
                    </div>
                    {month.actual_cost && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">{t('cashflow.actualCost')}</span>
                        <span className="font-medium text-red-600">{formatCurrency(month.actual_cost)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">{t('cashflow.netCashFlowLabel')}</span>
                    <span className={`font-bold ${
                      month.planned_income - month.planned_cost >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(month.planned_income - month.planned_cost)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Collection Metrics */}
        {cashflowData?.cumulative && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('cashflow.collectionPerformance')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">{t('cashflow.collectionRate')}</span>
                  <span className="font-medium">
                    {((cashflowData.cumulative.total_collected / cashflowData.cumulative.total_invoiced) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full"
                    style={{ width: `${(cashflowData.cumulative.total_collected / cashflowData.cumulative.total_invoiced) * 100}%` }}
                  />
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">{t('cashflow.costVsRevenue')}</span>
                  <span className="font-medium">
                    {((cashflowData.cumulative.total_spent / cashflowData.cumulative.total_collected) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-500 rounded-full"
                    style={{ width: `${Math.min((cashflowData.cumulative.total_spent / cashflowData.cumulative.total_collected) * 100, 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CashFlow;
